import type { VoiceCallLocationState } from "@/modules/chat/voice-call/voice-call.utils";
import {
  useCreateThread,
} from "@/modules/assistants/threads/threads.api";
import { useAuth } from "@frontegg/react";
import { PROCESS_ID } from "@soulbyte/core";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

type StartVoiceCallParams = {
  threadId?: string | null;
  assistantId: string;
};

/**
 * Returns **`startVoiceCall`**, which navigates to `/chat` with `assistantId` / `threadId`
 * query params and sets **`location.state.startVoiceCall`** so {@link useConsumeVoiceCallLocationState}
 * in `ChatContent` can flip **`isVoiceCall`** on `ChatContentContext`.
 *
 * When `threadId` is omitted, creates a draft thread first (requires create entitlement).
 */
export function useStartVoiceCall() {
  const { user } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const { mutateAsync, isPending: isCreatingThread, isEntitled: isEntitledToCreateThread } = useCreateThread();

  const startVoiceCall = useCallback(
    async ({ assistantId, threadId }: StartVoiceCallParams) => {
      const userId = user?.id;
      if (!userId) return;

      const prevState =
        location.state && typeof location.state === "object"
          ? (location.state as object)
          : {};

      // If threadId is provided, navigate to the chat page with the threadId
      if (threadId) {
        navigate(`/chat?assistantId=${assistantId}&threadId=${threadId}`, {
          replace: true,
          state: {
            ...prevState,
            startVoiceCall: true,
          } satisfies VoiceCallLocationState,
        });
        return;
      }

      if (!isEntitledToCreateThread) return;

      // If threadId is not provided, create a new thread
      const data = await mutateAsync({
        params: {
          path: {
            assistant_id: assistantId,
          },
        },
        body: {
          user_id: userId,
          title: "New Chat",
          stage: "draft",
          process_id: PROCESS_ID,
        },
      });

      const thread = data?.data?.thread;
      if (!thread) return;

      navigate(`/chat?assistantId=${assistantId}&threadId=${thread.id}`, {
        state: { startVoiceCall: true } satisfies VoiceCallLocationState,
      });
    },
    [
      user?.id,
      navigate,
      mutateAsync,
      location.state,
      isEntitledToCreateThread,
    ],
  );

  return {
    startVoiceCall,
    isStartingVoiceCall: isCreatingThread,
    isEntitledToStartVoiceCall: isEntitledToCreateThread,
  };
}

/**
 * When `location.state.startVoiceCall` is set (e.g. after {@link useStartVoiceCall}
 * navigates to chat), runs `onConsumed` once and `replace`s the current history entry
 * so the flag is cleared while any other `location.state` keys are preserved.
 *
 * @param onConsumed - Side effect to run when the voice-call navigation intent is
 * present (typically toggling UI into the voice-call view).
 */
export function useConsumeVoiceCallLocationState(onConsumed: () => void) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(
    function consumeStartVoiceCallNavigationIntent() {
      const state = location.state as VoiceCallLocationState | null;
      if (!state?.startVoiceCall) return;
      onConsumed();

      const rest = { ...state } as Record<string, unknown>;
      delete rest.startVoiceCall;
      const restKeys = Object.keys(rest);

      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        { replace: true, state: restKeys.length > 0 ? rest : null },
      );
    },
    [
      location.hash,
      location.pathname,
      location.search,
      location.state,
      navigate,
      onConsumed,
    ],
  );
}
